/**
 * Lead tier badge — color-coded chip for cold/warm/hot/red_hot.
 *
 * Color philosophy matches the public funnel result page so admins see the
 * same hue the visitor saw.
 */
import React from 'react';
import { Snowflake, Sun, Flame, Zap } from 'lucide-react';
import type { LeadTier } from '../types/lead';

interface LeadTierBadgeProps {
  tier: LeadTier;
  size?: 'sm' | 'md' | 'lg';
  withIcon?: boolean;
  className?: string;
}

const TIER_STYLES: Record<LeadTier, string> = {
  cold: 'bg-gray-100 text-gray-700 border border-gray-200',
  warm: 'bg-amber-50 text-amber-800 border border-amber-200',
  hot: 'bg-orange-50 text-orange-800 border border-orange-200',
  red_hot: 'bg-red-50 text-red-700 border border-red-200',
};

const TIER_LABELS: Record<LeadTier, string> = {
  cold: 'Cold',
  warm: 'Warm',
  hot: 'Hot',
  red_hot: 'Red Hot',
};

const TIER_ICONS: Record<LeadTier, React.ComponentType<{ className?: string }>> = {
  cold: Snowflake,
  warm: Sun,
  hot: Flame,
  red_hot: Zap,
};

const SIZE_STYLES: Record<NonNullable<LeadTierBadgeProps['size']>, string> = {
  sm: 'px-2 py-0.5 text-xs rounded-full',
  md: 'px-2.5 py-1 text-sm rounded-full',
  lg: 'px-3 py-1.5 text-base rounded-lg',
};

const ICON_SIZES: Record<NonNullable<LeadTierBadgeProps['size']>, string> = {
  sm: 'w-3 h-3',
  md: 'w-3.5 h-3.5',
  lg: 'w-4 h-4',
};

const LeadTierBadge: React.FC<LeadTierBadgeProps> = ({
  tier,
  size = 'sm',
  withIcon = true,
  className = '',
}) => {
  const Icon = TIER_ICONS[tier];
  return (
    <span
      className={`inline-flex items-center font-medium ${SIZE_STYLES[size]} ${TIER_STYLES[tier]} ${className}`}
    >
      {withIcon && Icon && <Icon className={`${ICON_SIZES[size]} mr-1`} />}
      {TIER_LABELS[tier]}
    </span>
  );
};

export default LeadTierBadge;
